import { useState } from "react";
import { Link } from "react-router-dom";
import { ExternalLink, Sparkles, ScanText } from "lucide-react";

/**
 * SocialNetworksStrip — branded card row for every social network Newseze
 * supports for content analysis. Each card has:
 *   • Brand logo + display name
 *   • "Visit" outbound link (nofollow + no-referrer)
 *   • "Analyze a post" → /lens?url=... (uses the network's URL pattern)
 *
 * Order (per editorial directive 2026-06-16):
 *   1. X (Twitter)
 *   2. Meta — Facebook + Instagram + Threads grouped as one Meta tile that
 *      expands into the three sub-platforms
 *   3. TikTok
 *   4. YouTube
 *   5. Truth Social  ← user-requested: in top 5 but NOT #1
 *   ... then Reddit, LinkedIn, Bluesky, Mastodon, Rumble, Substack
 */
const NETWORKS = [
  {
    key: "x",
    name: "X",
    handle: "x.com",
    color: "bg-black text-white",
    iconBg: "bg-black",
    visitUrl: "https://x.com",
    samplePrefix: "https://x.com/",
    tagline: "Short-form posts · primary news firehose",
  },
  {
    key: "meta",
    name: "Meta",
    handle: "Facebook · Instagram · Threads",
    color: "bg-gradient-to-br from-[#1877f2] to-[#e4405f] text-white",
    iconBg: "bg-[#1877f2]",
    visitUrl: "https://www.facebook.com",
    samplePrefix: "https://www.facebook.com/",
    tagline: "Meta networks — FB · IG · Threads",
    subPlatforms: [
      { key: "facebook", name: "Facebook", visitUrl: "https://www.facebook.com" },
      { key: "instagram", name: "Instagram", visitUrl: "https://www.instagram.com" },
      { key: "threads", name: "Threads", visitUrl: "https://www.threads.net" },
    ],
  },
  {
    key: "tiktok",
    name: "TikTok",
    handle: "tiktok.com",
    color: "bg-black text-white",
    iconBg: "bg-gradient-to-br from-[#25f4ee] via-black to-[#fe2c55]",
    visitUrl: "https://www.tiktok.com",
    samplePrefix: "https://www.tiktok.com/@",
    tagline: "Short-form video · viral framing",
  },
  {
    key: "youtube",
    name: "YouTube",
    handle: "youtube.com",
    color: "bg-[#ff0000] text-white",
    iconBg: "bg-[#ff0000]",
    visitUrl: "https://www.youtube.com",
    samplePrefix: "https://www.youtube.com/watch?v=",
    tagline: "Long-form video · description scored",
  },
  {
    key: "truth_social",
    name: "Truth Social",
    handle: "truthsocial.com",
    color: "bg-gradient-to-br from-[#5444de] to-[#0a1f3d] text-white",
    iconBg: "bg-[#5444de]",
    visitUrl: "https://truthsocial.com",
    samplePrefix: "https://truthsocial.com/",
    tagline: "Short-form posts · scored neutrally",
  },
  {
    key: "reddit",
    name: "Reddit",
    handle: "reddit.com",
    color: "bg-[#ff4500] text-white",
    iconBg: "bg-[#ff4500]",
    visitUrl: "https://www.reddit.com",
    samplePrefix: "https://www.reddit.com/r/",
    tagline: "Threads + top comments scored",
  },
  {
    key: "linkedin",
    name: "LinkedIn",
    handle: "linkedin.com",
    color: "bg-[#0a66c2] text-white",
    iconBg: "bg-[#0a66c2]",
    visitUrl: "https://www.linkedin.com",
    samplePrefix: "https://www.linkedin.com/posts/",
    tagline: "Business posts · careers",
  },
  {
    key: "bluesky",
    name: "Bluesky",
    handle: "bsky.app",
    color: "bg-[#0285ff] text-white",
    iconBg: "bg-[#0285ff]",
    visitUrl: "https://bsky.app",
    samplePrefix: "https://bsky.app/profile/",
    tagline: "Decentralized social · scored as post",
  },
  {
    key: "mastodon",
    name: "Mastodon",
    handle: "mastodon.social",
    color: "bg-[#6364ff] text-white",
    iconBg: "bg-[#6364ff]",
    visitUrl: "https://mastodon.social",
    samplePrefix: "https://mastodon.social/",
    tagline: "Federated social · scored as post",
  },
  {
    key: "rumble",
    name: "Rumble",
    handle: "rumble.com",
    color: "bg-[#85c742] text-[#0a1f3d]",
    iconBg: "bg-[#85c742]",
    visitUrl: "https://rumble.com",
    samplePrefix: "https://rumble.com/",
    tagline: "Video platform · description scored",
  },
  {
    key: "substack",
    name: "Substack",
    handle: "substack.com",
    color: "bg-[#ff6719] text-white",
    iconBg: "bg-[#ff6719]",
    visitUrl: "https://substack.com",
    samplePrefix: "https://",
    tagline: "Newsletter posts · opinion scored",
  },
];

export default function SocialNetworksStrip({ compact = false }) {
  const [expanded, setExpanded] = useState(null);

  return (
    <section className="rounded-2xl border border-[var(--line)] bg-white p-5 my-6" data-testid="social-networks-strip">
      <div className="flex items-start gap-3 mb-4 flex-wrap">
        <div className="flex-1 min-w-[280px]">
          <div className="text-[11px] uppercase tracking-[0.2em] font-black text-[var(--gold)] flex items-center gap-1.5 mb-1">
            <Sparkles size={11} /> Score any social post · 11 networks
          </div>
          <h3 className="text-[20px] font-black tracking-[-0.02em] leading-tight">
            Paste a post · we score the claim · no AI hype, just Newseze's algorithm.
          </h3>
          <p className="text-[12.5px] text-[var(--muted)] mt-1.5 leading-snug">
            Drop any link — X, Meta, TikTok, YouTube, Truth Social, Reddit and more — into Newseze Lens
            and we'll return Trust · Drama · Respect · Conspiracy scores plus a calm narrative.
          </p>
        </div>
        <Link to="/lens" className="btn primary self-start" data-testid="social-strip-lens">
          <ScanText size={13} /> Open Newseze Lens
        </Link>
      </div>

      <div className={`grid ${compact ? "grid-cols-2 sm:grid-cols-3 md:grid-cols-6" : "grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6"} gap-2`}>
        {NETWORKS.map((n) => (
          <div key={n.key} className="rounded-xl border border-[var(--line)] bg-white overflow-hidden" data-testid={`social-card-${n.key}`}>
            <div className={`${n.color} px-3 py-2.5 flex items-center justify-between`}>
              <div>
                <div className="text-[12px] font-black tracking-tight leading-none">{n.name}</div>
                <div className="text-[9.5px] uppercase tracking-[0.12em] opacity-80 mt-0.5">{n.handle}</div>
              </div>
              <a
                href={n.visitUrl}
                target="_blank"
                rel="nofollow noopener noreferrer"
                referrerPolicy="no-referrer"
                aria-label={`Visit ${n.name}`}
                data-testid={`social-visit-${n.key}`}
                className="opacity-80 hover:opacity-100"
              >
                <ExternalLink size={13} />
              </a>
            </div>
            <div className="p-2.5">
              <p className="text-[11px] text-[var(--muted)] leading-snug mb-2 min-h-[28px]">{n.tagline}</p>
              <Link
                to={`/lens?url=${encodeURIComponent(n.samplePrefix)}`}
                className="block w-full text-center text-[11px] font-black uppercase tracking-[0.14em] rounded-md py-1.5 bg-[var(--ink)] text-white hover:opacity-90"
                data-testid={`social-analyze-${n.key}`}
              >
                Analyze a post
              </Link>
              {n.subPlatforms && (
                <button
                  type="button"
                  onClick={() => setExpanded(expanded === n.key ? null : n.key)}
                  className="block w-full text-center text-[10px] font-bold text-[var(--blue)] mt-1 hover:underline"
                  data-testid={`social-expand-${n.key}`}
                >
                  {expanded === n.key ? "Hide" : "See sub-platforms"}
                </button>
              )}
              {n.subPlatforms && expanded === n.key && (
                <div className="mt-2 space-y-1" data-testid={`social-sub-${n.key}`}>
                  {n.subPlatforms.map((s) => (
                    <a
                      key={s.key}
                      href={s.visitUrl}
                      target="_blank"
                      rel="nofollow noopener noreferrer"
                      referrerPolicy="no-referrer"
                      className="block text-[11px] text-[var(--ink)] hover:text-[var(--blue)] border-t border-[var(--line)] pt-1"
                      data-testid={`social-sub-link-${s.key}`}
                    >
                      → {s.name}
                    </a>
                  ))}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      <p className="text-[10.5px] text-[var(--muted)] italic mt-4 border-t border-[var(--line)] pt-3">
        Newseze does not endorse any platform. Scoring is applied uniformly to every network — no
        platform-based favoritism, no AI mystique, just our editorial algorithm.
      </p>
    </section>
  );
}
